import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import CertificatesForm from './CertificatesForm';
import { Edit, X } from 'lucide-react';

const CertificatesSection = ({ db, onComplete }) => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newCertificate, setNewCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCertificates = async () => {
      try {
        const pilotRef = doc(db, 'pilot', user.uid);
        const docSnap = await getDoc(pilotRef);
        if (docSnap.exists() && docSnap.data().certificates) {
          setCertificates(docSnap.data().certificates.map((cert, index) => ({
            ...cert,
            id: cert.id || `cert-${index}`,
          })));
        }
      } catch (err) {
        console.error('Error loading certificates:', err);
        setError('Failed to load certificates.');
      } finally {
        setLoading(false);
      }
    };
    loadCertificates();
  }, [db, user]);

  const handleSubmit = async (formData) => {
    try {
      const updatedCertificates = editingIndex !== null
        ? certificates.map((cert, index) => (index === editingIndex ? formData : cert))
        : [...certificates, { ...formData, id: `cert-${Date.now()}` }];

      const pilotRef = doc(db, 'pilot', user.uid);
      await updateDoc(pilotRef, { certificates: updatedCertificates });

      setCertificates(updatedCertificates);
      setEditingIndex(null);
      setNewCertificate(null);
      onComplete?.();
    } catch (err) {
      console.error('Error saving certificate:', err);
      setError('Failed to save certificate.');
    }
  };

  const handleRemove = async (index) => {
    try {
      const updatedCertificates = certificates.filter((_, i) => i !== index);

      const pilotRef = doc(db, 'pilot', user.uid);
      await updateDoc(pilotRef, { certificates: updatedCertificates });

      setCertificates(updatedCertificates);
    } catch (err) {
      console.error('Error removing certificate:', err);
      setError('Failed to remove certificate.');
    }
  };

  const startEdit = (cert, index) => {
    setNewCertificate(cert);
    setEditingIndex(index);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      {newCertificate ? (
        <CertificatesForm
          initialData={newCertificate}
          onSubmit={handleSubmit}
          onCancel={() => {
            setNewCertificate(null);
            setEditingIndex(null);
          }}
        />
      ) : (
        <button
          onClick={() => {
            setNewCertificate({ type: '', issueDate: '', ratings: [] });
            setEditingIndex(null);
          }}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          Add Certificate/Rating
        </button>
      )}

      <div className="mt-4">
        {certificates.map((cert, index) => (
          <div key={cert.id || `cert-${index}`} className="bg-neutral-white p-4 rounded-lg mb-2 flex justify-between items-center">
            <div>
              <p className="font-medium">{cert.type}</p>
              <p className="text-sm text-gray-600">Issued: {new Date(cert.issueDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}</p>
              <p className="text-sm font-medium text-gray-600">Ratings:</p>
                <ul className="list-disc pl-5 text-sm text-gray-600">
                  {cert.ratings && cert.ratings.length > 0 ? (
                    cert.ratings.map((rating, index) => (
                      <li key={index}>{rating}</li>
                    ))
                  ) : (
                    <li>No ratings available</li>
                  )}
                </ul>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => startEdit(cert, index)}
                className="text-blue-500 hover:text-blue-700"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleRemove(index)}
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CertificatesSection;
